import type { ElementType } from "react";

import {
  AgentsIcon,
  ArchiveIcon,
  AssetsIcon,
  AudienceIcon,
  BrainIcon,
  BrandIcon,
  BuildingIcon,
  Column,
  ColumnChartIcon,
  ConnectionIcon,
  ExtensionsIcon,
  FlowsIcon,
  FoldersIcon,
  HistoryIcon,
  KeyIcon,
  LawScaleIcon,
  LinkIcon,
  LockIcon,
  SettingsIcon,
  SourceIcon,
  StarBadgeIcon,
  TagIcon,
  TemplatesIcon,
  UserIcon,
} from "@hightouch/htui";

import { cachedNavIcon, navIcon } from "src/components/layout/nav/nav-icon";
import { NavItem } from "src/components/layout/nav/nav-item";
import { useShowAgentsInNav } from "src/components/layout/nav/sections/hooks/agents";
import {
  CUSTOM_AGENTS_PATH,
  useContextLinks,
} from "src/components/layout/nav/sections/hooks/context";
import { useProjectsAccess } from "src/components/layout/nav/sections/hooks/projects";
import {
  AD_STUDIO_TEMPLATES_PATH,
  EMAIL_TEMPLATES_PATH,
} from "src/components/template-library/use-template-library-access";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useSpaceContext } from "src/components/spaces";
import { useHasOrganizationSettings } from "src/hooks/use-has-organization-settings";
import {
  AGENT_SETTINGS_TABS,
  agentSettingsNavTitle,
} from "src/pages/agents/settings/tabs";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import {
  PROJECTS_PATHS,
  PROJECTS_TERMS as TERMS,
} from "src/pages/projects/constants";
import { useWorkspaceSettingsTabs } from "src/pages/settings/tabs";
import { useMatch } from "src/router";

const WORKSPACE_SETTINGS_ICONS = new Map<string, ElementType>([
  ["workspace", navIcon(SettingsIcon)],
  ["members", navIcon(UserIcon)],
  ["groups", navIcon(AudienceIcon)],
  ["spaces", navIcon(FoldersIcon)],
  ["cloud-providers", navIcon(ConnectionIcon)],
  ["api-keys", navIcon(KeyIcon)],
  ["storage", navIcon(ArchiveIcon)],
  ["tunnels", navIcon(LinkIcon)],
  ["labels", navIcon(TagIcon)],
  ["audit-log", navIcon(HistoryIcon)],
  ["entitlement-center", navIcon(StarBadgeIcon)],
  ["support-access", navIcon(LockIcon)],
]);
const CONTEXT_ICONS = new Map<string, ElementType>([
  ["/context-hub/brands", navIcon(BrandIcon)],
  ["/context-hub/guardrails", navIcon(LawScaleIcon)],
  ["/context-hub/assets", navIcon(AssetsIcon)],
  ["/context-hub/connected-data", navIcon(SourceIcon)],
  [AD_STUDIO_TEMPLATES_PATH, navIcon(TemplatesIcon)],
  [EMAIL_TEMPLATES_PATH, navIcon(TemplatesIcon)],
  [CUSTOM_AGENTS_PATH, navIcon(AgentsIcon)],
]);
const AGENT_SETTINGS_ICONS = new Map<string, ElementType>([
  ["general", navIcon(BrainIcon)],
  ["connectors", navIcon(ExtensionsIcon)],
  ["usage", navIcon(ColumnChartIcon)],
]);
const DEFAULT_ICON = navIcon(SettingsIcon);
// The workspace's own settings and its members lead the list, ahead of the project and agent settings.
const LEAD_WORKSPACE_TABS = new Set(["workspace", "members"]);

const PROJECT_SETTINGS_LINKS = [
  {
    href: `${PROJECTS_PATHS.settings}/workflows`,
    label: TERMS.workflow.Plural,
    icon: navIcon(FlowsIcon),
  },
  {
    href: `${PROJECTS_PATHS.settings}/tags`,
    label: TERMS.tag.Plural,
    icon: navIcon(TagIcon),
  },
];

/** The full-screen settings' pages: project, agent, workspace, and organization settings in one list. */
export const useSettingsPages = () => {
  const tabs = useWorkspaceSettingsTabs();
  const { hasAccess: hasOrganizationSettings } = useHasOrganizationSettings();
  const { isEnabled: hasProjects } = useProjectsAccess();
  const { isPermitted: canConfigureAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_configure_agents" },
  });
  const showAgents = useShowAgentsInNav();
  const isAgentsEnabled = useAgentsEnabledFlag();
  const { isInSpace, isPathAccessibleInSpace } = useSpaceContext();
  const showWorkspaceSettings =
    !isInSpace || isPathAccessibleInSpace("/settings");

  const workspaceLinks = showWorkspaceSettings
    ? tabs
        .filter((tab) => !tab.isHidden)
        .map((tab) => ({
          href: `/settings/${tab.path}`,
          label: tab.title,
          icon: WORKSPACE_SETTINGS_ICONS.get(tab.path) ?? DEFAULT_ICON,
          isLead: LEAD_WORKSPACE_TABS.has(tab.path),
        }))
    : [];
  const agentLinks =
    showAgents && isAgentsEnabled && canConfigureAgents
      ? AGENT_SETTINGS_TABS.map((tab) => ({
          href: `/agents/settings/${tab.path}`,
          // In one list with the workspace's settings, the agent's configuration needs the agent in its name.
          label:
            tab.path === "general"
              ? "Agent settings"
              : agentSettingsNavTitle(tab),
          icon: AGENT_SETTINGS_ICONS.get(tab.path) ?? DEFAULT_ICON,
        }))
      : [];
  return [
    ...workspaceLinks.filter(({ isLead }) => isLead),
    ...(hasProjects ? PROJECT_SETTINGS_LINKS : []),
    ...agentLinks,
    ...workspaceLinks.filter(({ isLead }) => !isLead),
    ...(hasOrganizationSettings
      ? [
          {
            href: "/organization",
            label: "Organization",
            icon: navIcon(BuildingIcon),
          },
        ]
      : []),
  ];
};

/** The full-screen settings' sidebar. */
export const SettingsNav = () => {
  const links = useSettingsPages();

  return (
    <Column gap={1}>
      {links.map(({ href, label, icon }) => (
        <SettingsLink key={href} href={href} label={label} icon={icon} />
      ))}
    </Column>
  );
};

/** Context's sidebar in the full-screen settings: Context Hub's pages, the template library, then the agent's pages. */
export const ContextNav = () => {
  const { context, templates, agents } = useContextLinks();

  return (
    <Column gap={1}>
      {[...context, ...(templates ? [templates] : []), ...agents].map(
        (link) => (
          <SettingsLink
            key={link.href}
            {...link}
            icon={
              link.icon
                ? cachedNavIcon(link.icon)
                : (CONTEXT_ICONS.get(link.href) ?? DEFAULT_ICON)
            }
          />
        ),
      )}
    </Column>
  );
};

type SettingsLinkProps = {
  href: string;
  label: string;
  icon: ElementType;
  /** Another page that lights the entry, such as the template library's other tab. */
  alsoActiveOn?: string;
};

const SettingsLink = ({
  href,
  label,
  icon,
  alsoActiveOn,
}: SettingsLinkProps) => {
  // The splat keeps an entry lit on the pages nested under it.
  const isOnHref = useMatch(`${href}/*`) !== null;
  const isOnOther = useMatch(`${alsoActiveOn ?? href}/*`) !== null;

  return (
    <NavItem
      href={href}
      isActive={isOnHref || isOnOther}
      icon={icon}
      label={label}
    />
  );
};
